import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ImageUp, RotateCcw } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { shrinkImage as shrink } from "@/lib/images";
import { COUNTRIES, ZONES } from "@/data/site";
import { PHOTOS_QUERY, usePhotos } from "@/hooks/usePhotos";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

function PhotoCard({ photoKey, label, src, custom, wide }) {
  const qc = useQueryClient();
  const input = useRef(null);
  const [progress, setProgress] = useState(null);
  const id = slug(photoKey);

  const upload = async (e) => {
    const picked = e.target.files?.[0];
    e.target.value = "";
    if (!picked) return;
    try {
      setProgress(0);
      const fd = new FormData();
      fd.append("key", photoKey);
      fd.append("file", await shrink(picked));
      await api.post("/admin/photos", fd, { onUploadProgress: (p) => setProgress(Math.round((p.progress ?? 0) * 100)) });
      await qc.invalidateQueries({ queryKey: PHOTOS_QUERY });
      toast.success(`${label} photo updated`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setProgress(null);
    }
  };

  const reset = async () => {
    try {
      await api.delete("/admin/photos", { params: { key: photoKey } });
      await qc.invalidateQueries({ queryKey: PHOTOS_QUERY });
      toast.success(`${label} photo reset to original`);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div className={`rounded-2xl border border-cream/10 bg-cream/5 overflow-hidden ${wide ? "sm:col-span-2 lg:col-span-4" : ""}`} data-testid={`photo-card-${id}`}>
      <div className={`relative bg-cream/10 ${wide ? "aspect-[21/8]" : "aspect-[4/3]"}`}>
        {src && <img src={src} alt={label} className="absolute inset-0 h-full w-full object-cover" data-testid={`photo-img-${id}`} />}
        {custom && <span className="absolute top-3 left-3 rounded-full bg-gold text-ink text-xs font-semibold px-2.5 py-1" data-testid={`photo-custom-badge-${id}`}>Your photo</span>}
        {progress !== null && (
          <div className="absolute inset-0 grid place-items-center bg-ink/70 text-cream font-mono text-sm" data-testid={`photo-progress-${id}`}>Uploading {progress}%</div>
        )}
      </div>
      <div className="flex items-center justify-between gap-2 p-3">
        <p className="font-display font-bold truncate">{label}</p>
        <div className="flex items-center gap-1.5 shrink-0">
          {custom && (
            <button onClick={reset} disabled={progress !== null} title="Reset to original" data-testid={`photo-reset-${id}`}
              className="rounded-full border border-cream/20 p-2 text-cream/80 hover:bg-cream hover:text-ink transition-colors disabled:opacity-40">
              <RotateCcw className="h-4 w-4" />
            </button>
          )}
          <button onClick={() => input.current?.click()} disabled={progress !== null} data-testid={`photo-upload-${id}`}
            className="inline-flex items-center gap-1.5 rounded-full bg-terra px-3.5 py-2 text-sm font-semibold text-paper hover:bg-terra-soft transition-colors disabled:opacity-40">
            <ImageUp className="h-4 w-4" /> Upload
          </button>
          <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={upload} data-testid={`photo-input-${id}`} />
        </div>
      </div>
    </div>
  );
}

export function PhotosManager() {
  const [zone, setZone] = useState(ZONES[0].slug);
  const photos = usePhotos();
  const zoneObj = ZONES.find((z) => z.slug === zone);
  const countries = COUNTRIES.filter(([, z]) => z === zone);

  return (
    <div data-testid="photos-manager">
      <p className="text-cream/70 max-w-2xl">Pick a region, then upload a new photo for the region banner or any country tile. Changes show on the website straight away. Use the reset button to bring back the original photo.</p>
      <div className="mt-6 flex gap-1.5 overflow-x-auto no-scrollbar pb-1" data-testid="photos-region-rail">
        {ZONES.map((z) => (
          <button key={z.slug} onClick={() => setZone(z.slug)} data-testid={`photos-region-${z.slug}`}
            className={`shrink-0 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${zone === z.slug ? "bg-cream text-ink" : "border border-cream/15 text-cream/75 hover:text-cream"}`}>
            {z.name}
          </button>
        ))}
      </div>
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <PhotoCard wide photoKey={`region:${zone}`} label={`${zoneObj?.name} banner`} src={photos.region(zone)} custom={photos.isCustom(`region:${zone}`)} />
        {countries.map(([name]) => (
          <PhotoCard key={name} photoKey={`country:${name}`} label={name} src={photos.country(name)} custom={photos.isCustom(`country:${name}`)} />
        ))}
      </div>
    </div>
  );
}
